import { useState, useEffect } from "react";
import { AppLayout } from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Landmark, Clock, ShieldAlert, CheckCircle2, XCircle, ArrowRight } from "lucide-react";

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

const STATUS_MAP = {
  pending:    { c: "bg-amber-500/10 text-amber-300 border-amber-500/40", l: "Pending review" },
  approved:   { c: "bg-[#8AE234]/10 text-[#8AE234] border-[#8AE234]/40", l: "Approved · Active" },
  target_met: { c: "bg-cyan-500/10 text-cyan-300 border-cyan-500/40", l: "Target Met · Profit Released" },
  rejected:   { c: "bg-red-500/10 text-red-300 border-red-500/40", l: "Rejected" },
  expired:    { c: "bg-slate-500/10 text-slate-300 border-slate-500/40", l: "Expired" },
  cleared:    { c: "bg-cyan-500/10 text-cyan-300 border-cyan-500/40", l: "Cleared / Fully Repaid" },
};

function StatusPill({ status }) {
  const it = STATUS_MAP[status] || STATUS_MAP.pending;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${it.c}`}>{it.l}</span>;
}

export default function Credit() {
  const [cfg, setCfg] = useState(null);
  const [my, setMy] = useState(null);
  const [amount, setAmount] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [c1, c2] = await Promise.all([
        api.get("/credit/config").then(r => r.data).catch(() => null),
        api.get("/credit/my").then(r => r.data).catch(() => null),
      ]);
      setCfg(c1); setMy(c2?.credit_line || null);
      // Default to the smallest slab, or min_amount if no slabs configured
      if (c1 && !amount) {
        const firstSlab = (c1.slabs && c1.slabs.length) ? c1.slabs[0].amount : c1.min_amount;
        setAmount(String(firstSlab));
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // load on mount

  const requestOtp = async () => {
    if (!agree) return toast.error("Accept the Credit Terms & Conditions first.");
    const amt = parseFloat(amount);
    if (isNaN(amt)) return toast.error("Enter a valid amount.");
    setBusy(true);
    try {
      await api.post("/credit/request-otp");
      setOtpSent(true);
      toast.success("OTP sent to your registered email");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitApplication = async () => {
    const amt = parseFloat(amount);
    if (isNaN(amt)) return toast.error("Enter a valid amount.");
    if (otp.length < 6) return toast.error("Enter the 6-digit OTP.");
    setBusy(true);
    try {
      const { data } = await api.post("/credit/apply", { amount: amt, accepted_terms: true, otp: otp.trim() });
      toast.success(`Credit line of $${data.amount} activated for ${data.validity_days} days`);
      setOtpSent(false); setOtp(""); setAgree(false);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Feb-21: Slab-driven UI. If Admin has configured slabs, we render them as radio-style tiles.
  const slabs = cfg?.slabs || [];
  const hasSlabs = slabs.length > 0;
  const mult = cfg?.business_multiplier ?? 10;
  const shareP = cfg?.self_direct_share_pct ?? 50;
  const teamP = 100 - shareP;
  const selectedSlab = hasSlabs ? slabs.find(s => Number(s.amount) === Number(amount)) : null;
  // Build the dynamic terms — includes the CURRENT admin-configured slabs & multiplier & split.
  // These terms will be persisted verbatim in the credit_lines meta on apply so that even after
  // Admin updates settings, an already-issued credit shows the rules it was issued under.
  const termsList = [
    { key: "instant", txt: <>Credit is <b>instantly activated</b> after OTP verification — no manual approval, no waiting.</> },
    { key: "nowd",    txt: <>Credit Amount is <b>never withdrawable</b>, under any condition — including if you meet the {mult}× target.</> },
    { key: "target",  txt: <>Only Trading Profit earned from this credit becomes yours — and only if you complete <b>{mult}× credit business volume</b> in your network, split as <b>{shareP}% self + direct</b> and <b>up to {teamP}% team</b>, within {cfg?.validity_days || 60} days.</> },
    { key: "hold",    txt: <>Credit-earned profit stays as <b>Hold Profit</b> until the {mult}× target is met (releases instantly on hit) or the {cfg?.validity_days || 60}-day settlement.</> },
    { key: "burn",    txt: <>If the {mult}× target with the {shareP}/{teamP} split is NOT met → credit AND its hold profit both expire &amp; burn. Your normal team/direct/level commissions are <b>not affected</b>.</> },
    { key: "excl",    txt: <>Credit does not count for tour eligibility, staking, or generate upline commissions.</> },
    { key: "once",    txt: <>Credit Line is a <b>one-time facility</b> — you can apply for it only once in your lifetime.</> },
  ];

  return (
    <AppLayout>
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="credit-page">
        <div className="nb-card p-6">
          <div className="flex items-center gap-2 text-xs text-[#EAB308] uppercase tracking-widest font-bold">
            <Landmark size={14}/> Company Credit Line
          </div>
          <h1 className="font-heading text-white text-3xl mt-2">Apply for Company Credit</h1>
          <p className="text-slate-300 text-sm mt-2 max-w-2xl">
            Boost your trading capacity with a company-funded credit line. Credit is separate from your real balance and
            is intended <span className="text-[#EAB308] font-semibold">only for eligible trading & network activity</span>.
          </p>
        </div>

        {loading ? <div className="text-slate-400 text-sm px-2">Loading…</div> : null}

        {my ? (
          <div className="nb-card p-6" data-testid="credit-my">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
              <div className="font-heading text-white font-semibold">Your Credit Line</div>
              <StatusPill status={my.status}/>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><div className="text-slate-500 text-[10px] uppercase tracking-widest">Credit</div><div className="font-mono text-white">{fmt(my.amount)}</div></div>
              <div><div className="text-slate-500 text-[10px] uppercase tracking-widest">{Math.round(my.business_volume_multiplier || mult)}× Target</div><div className="font-mono text-[#EAB308]">{fmt(my.business_volume_target)}</div></div>
              <div><div className="text-slate-500 text-[10px] uppercase tracking-widest">Business Done</div><div className="font-mono text-[#8AE234]">{fmt(my.business_volume_generated)}</div></div>
              <div><div className="text-slate-500 text-[10px] uppercase tracking-widest">Hold Profit</div><div className="font-mono text-cyan-300">{fmt(my.hold_profit)}</div></div>
            </div>
            {my.status === "approved" && (
              <div className="mt-4 space-y-3">
                <div>
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-500">
                    <span>Overall Progress ({shareP}/{teamP} rule applied)</span>
                    <span className="font-mono text-white">{Number(my.business_volume_progress_pct||0).toFixed(1)}%</span>
                  </div>
                  <div className="mt-1 h-2 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full bg-[#8AE234]" style={{width: `${Math.min(100, Number(my.business_volume_progress_pct||0))}%`}}/>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-[11px]">
                  <div className="rounded-lg border border-[#8AE234]/30 bg-[#8AE234]/5 p-2">
                    <div className="text-slate-400 uppercase tracking-widest text-[9px]">Self + Direct ({shareP}%)</div>
                    <div className="font-mono text-[#8AE234] mt-0.5">{fmt(my.self_direct_volume_generated)} / {fmt(my.self_direct_half_target)}</div>
                  </div>
                  <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-2">
                    <div className="text-slate-400 uppercase tracking-widest text-[9px]">Team ({teamP}%)</div>
                    <div className="font-mono text-cyan-300 mt-0.5">{fmt(my.team_volume_generated)} / {fmt(my.team_half_target)}</div>
                  </div>
                </div>
              </div>
            )}
            <div className="text-xs text-slate-400 mt-3">
              {my.expires_at ? <>Expires: <span className="text-white font-mono">{new Date(my.expires_at).toLocaleString()}</span></> : <>Awaiting approval</>}
            </div>

            {/* Feb-21: Persist the terms permanently even after credit is issued */}
            <div className="mt-5 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-[13px] text-amber-200 space-y-1.5" data-testid="credit-terms-locked">
              <div className="flex items-center gap-2 font-bold text-xs uppercase tracking-widest text-amber-300"><ShieldAlert size={13}/> Terms of your active Credit</div>
              <ul className="list-disc pl-5 space-y-1 text-xs">
                {termsList.map(t => <li key={t.key}>{t.txt}</li>)}
              </ul>
            </div>
          </div>
        ) : null}

        {cfg?.enabled && !my ? (
          <div className="nb-card p-6" data-testid="credit-apply-form">
            <div className="font-heading text-white font-semibold mb-3">Submit New Application</div>
            <div className="text-[11px] text-amber-300/80 -mt-2 mb-3">⚠️ Credit Line is a <b>one-time facility</b> — you can apply for it only once in your lifetime. Choose your slab carefully.</div>

            {hasSlabs ? (
              <div className="mb-4">
                <label className="text-xs text-slate-400 uppercase tracking-widest">Choose your credit slab</label>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="credit-slabs-grid">
                  {slabs.map((s) => {
                    const isSel = Number(amount) === Number(s.amount);
                    return (
                      <button
                        type="button"
                        key={s.amount}
                        data-testid={`credit-slab-${s.amount}`}
                        onClick={() => setAmount(String(s.amount))}
                        className={`rounded-xl border p-3 text-left transition-all ${isSel ? "border-[#8AE234] bg-[#8AE234]/10 shadow-[0_0_18px_rgba(138,226,52,0.25)]" : "border-white/10 bg-white/[0.02] hover:border-[#8AE234]/40"}`}
                      >
                        <div className="text-[10px] uppercase tracking-widest text-slate-400">Credit</div>
                        <div className={`font-mono text-2xl font-bold ${isSel ? "text-[#8AE234]" : "text-white"}`}>${Number(s.amount).toFixed(0)}</div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{s.business_multiplier}× Target</div>
                        <div className="font-mono text-[11px] text-[#EAB308]">${Number(s.business_volume_target).toFixed(0)}</div>
                      </button>
                    );
                  })}
                </div>
                {selectedSlab && (
                  <div className="mt-3 rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3 text-xs text-cyan-200" data-testid="credit-selected-slab">
                    <div className="font-bold text-cyan-300 uppercase tracking-widest text-[10px]">Selected Slab Requirement</div>
                    <div className="mt-1">Build <b>${Number(selectedSlab.business_volume_target).toFixed(0)}</b> business in {cfg.validity_days} days → <b className="text-[#8AE234]">${(selectedSlab.business_volume_target * shareP / 100).toFixed(0)} ({shareP}%)</b> from Self+Direct + <b className="text-cyan-300">${(selectedSlab.business_volume_target * teamP / 100).toFixed(0)} ({teamP}%)</b> from Team.</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div className="md:col-span-2">
                  <label className="text-xs text-slate-400 uppercase tracking-widest">Credit Amount (${cfg.min_amount} – ${cfg.max_amount})</label>
                  <input
                    data-testid="credit-amount"
                    type="number"
                    min={cfg.min_amount}
                    max={cfg.max_amount}
                    step="10"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    className="nb-input mt-1 font-mono"
                    placeholder={`${cfg.min_amount}`}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-widest">Validity</label>
                  <div className="nb-input mt-1 flex items-center gap-2 font-mono">
                    <Clock size={14} className="text-cyan-300"/> {cfg.validity_days} days
                  </div>
                </div>
              </div>
            )}

            <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-[13px] text-amber-200 space-y-1.5" data-testid="credit-terms">
              <div className="flex items-center gap-2 font-bold text-xs uppercase tracking-widest text-amber-300"><ShieldAlert size={13}/> Credit Terms & Conditions</div>
              <ul className="list-disc pl-5 space-y-1 text-xs">
                {termsList.map(t => <li key={t.key}>{t.txt}</li>)}
              </ul>
            </div>

            <label className="flex items-start gap-2 mt-4 text-slate-300 cursor-pointer" data-testid="credit-accept-label">
              <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} className="mt-0.5" data-testid="credit-accept"/>
              <span className="text-xs">I have read and accept the Credit Terms &amp; Conditions above.</span>
            </label>

            {!otpSent ? (
              <button
                data-testid="credit-request-otp"
                onClick={requestOtp}
                disabled={busy || !agree}
                className="mt-4 nb-btn-primary inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ArrowRight size={14}/> {busy ? "Sending OTP…" : "Send OTP to my email"}
              </button>
            ) : (
              <div className="mt-4 space-y-3">
                <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 text-xs text-cyan-300">
                  OTP sent to your registered email. Check inbox &amp; spam. Valid for 10 minutes.
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-widest">Enter 6-digit OTP</label>
                  <input
                    data-testid="credit-otp"
                    value={otp}
                    onChange={e => setOtp(e.target.value.replace(/\D/g, ""))}
                    maxLength={6}
                    className="nb-input mt-1 font-mono text-xl tracking-[0.4em] text-center"
                    placeholder="000000"
                  />
                </div>
                <button
                  data-testid="credit-submit"
                  onClick={submitApplication}
                  disabled={busy || otp.length < 6}
                  className="nb-btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <CheckCircle2 size={14}/> {busy ? "Activating…" : "Verify OTP & Activate Credit"}
                </button>
                <button
                  data-testid="credit-cancel-otp"
                  onClick={() => { setOtpSent(false); setOtp(""); }}
                  disabled={busy}
                  className="w-full text-xs text-slate-400 hover:text-white"
                >
                  Cancel &amp; edit amount
                </button>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </AppLayout>
  );
}
